import { type PopupProps, Popup, FADE_DURATION } from '@/components/Popups';
import type { LangCopy } from '@CS/components/TranslationProvider/types';
import { HeaderContainer, HotspotContent } from './Hotspot.styles';
import { getCampaignPublicData } from '@/redux/campaign/selectors';
import { getCampaignGames } from '@/redux/campaign/selectors';
import { activateOffer, openDeals } from '@/services/app/mcd';
import { useNavigateQuery, useTranslation } from '@CS/hooks';
import { useState, useEffect } from 'react';
import { Hotspots } from '@/assets/images';
import Button from '@CS/components/Button';
import { useTranslations } from '@/hooks';
import { useCurrentWeek } from '@/hooks';
import { connect } from 'react-redux';
import API from '@/services/api';

import type {
  EntityData,
  CampaignUnit,
  CampaignGame
} from '@/services/api/types';

type HotspotProps = PopupProps & {
  campaignData: object | null;
  games: CampaignGame[];
  deal?: CampaignUnit;
  data?: EntityData;
  hotspot: number;
};

const Hotspot = ({
  campaignData,
  hotspot,
  games,
  deal,
  data,
  ...props
}: HotspotProps) => {
  let gameIndex = hotspot - 2;
  const index = gameIndex * 6;

  // Update the game index to select the last game after week 1:
  const week = useCurrentWeek(API.getCampaignGame(0));
  if (week > 1 && !gameIndex) gameIndex = 3;

  const navigate = useNavigateQuery();
  const [close, setClose] = useState(false);
  const gameName = API.getGameName(gameIndex);

  const fanshopLink = useTranslation(
    (campaignData as { fanshopLink: LangCopy })?.fanshopLink
  );

  const [dealsTitle, dealsButton, dealsDescription] = useTranslations([
    '03HotspotPopupDealsTitle',
    '03HotspotPopupDealsButton',
    '03HotspotPopupDealsDescription'
  ]);

  const [shopTitle, shopButton, shopDescription] = useTranslations([
    '03HotspotPopupFanshopTitle',
    '03HotspotPopupFanshopButton',
    '03HotspotPopupFanshopDescription'
  ]);

  const gamesKeys = useTranslations([
    '03HotspotPopupBurgerBounceTitle',
    '03HotspotPopupBurgerBounceButton',
    '03HotspotPopupBurgerBounceDescription',

    '03HotspotPopupBurgerBounceDisabledTitle',
    '03HotspotPopupBurgerBounceDisabledButton',
    '03HotspotPopupBurgerBounceDisabledDescription',

    '03HotspotPopupRunBurgerTitle',
    '03HotspotPopupRunBurgerButton',
    '03HotspotPopupRunBurgerDescription',

    '03HotspotPopupRunBurgerDisabledTitle',
    '03HotspotPopupRunBurgerDisabledButton',
    '03HotspotPopupRunBurgerDisabledDescription',

    '03HotspotPopupTapSmashTitle',
    '03HotspotPopupTapSmashButton',
    '03HotspotPopupTapSmashDescription',

    '03HotspotPopupTapSmashDisabledTitle',
    '03HotspotPopupTapSmashDisabledButton',
    '03HotspotPopupTapSmashDisabledDescription'
  ]);

  const gameTitle = gamesKeys[index];
  const gameButton = gamesKeys[index + 1];
  const gameDescription = gamesKeys[index + 2];

  const disabledGameTitle = gamesKeys[index + 3];
  const disabledGameButton = gamesKeys[index + 4];
  const disabledGameDescription = gamesKeys[index + 5];

  const activateDeal = async () => {
    if (deal?.itemType) {
      const [loyalty, reward] = deal.itemType.split(':') || [];
      activateOffer(+loyalty, +reward).then(openDeals);
    }
  };

  const getActiveGame = () => {
    const { active, isStarted, isFinished } =
      games.find(({ slug }) => slug.includes(gameName)) ?? {};

    return active && isStarted && !isFinished;
  };

  const onClick = () =>
    ~setClose(true) &&
    setTimeout(async () => {
      switch (hotspot) {
        case 0:
          await activateDeal();
          break;

        case 1:
          location.href = fanshopLink;
          break;

        default:
          getActiveGame() &&
            setTimeout(() => navigate(`/game/${gameIndex}`), FADE_DURATION);
      }
    }, FADE_DURATION);

  const [translation, setTranslation] = useState({
    description: gameDescription,
    button: gameButton,
    title: gameTitle
  });

  const Header = !hotspot ? (
    <HeaderContainer $background={Hotspots[hotspot]}>
      <img alt={translation?.title} src={deal?.imageUrls[0]} />
    </HeaderContainer>
  ) : (
    <img alt={translation?.title} src={Hotspots[hotspot]} />
  );

  useEffect(() => {
    if (1 < hotspot && !getActiveGame()) {
      setTranslation({
        description: disabledGameDescription,
        button: disabledGameButton,
        title: disabledGameTitle
      });
    } else {
      const translation =
        hotspot === 0
          ? {
              description: dealsDescription,
              button: dealsButton,
              title: dealsTitle
            }
          : hotspot === 1
            ? {
                description: shopDescription,
                button: shopButton,
                title: shopTitle
              }
            : {
                description: gameDescription,
                button: gameButton,
                title: gameTitle
              };

      setTranslation(translation);
    }
  }, [hotspot]);

  return (
    <Popup header={Header} close={close} {...props}>
      {translation && (
        <HotspotContent>
          <h2>{translation.title}</h2>
          <p>{translation.description}</p>
          <Button text={translation.button} onClick={onClick} />
        </HotspotContent>
      )}
    </Popup>
  );
};

const mapStateToProps = (state: never) => ({
  campaignData: getCampaignPublicData(state),
  games: getCampaignGames(state)
});

export default connect(mapStateToProps)(Hotspot);
